'use client';

import type { ReactElement } from 'react';
import { useCallback, useEffect, useState } from 'react';
import type { AnimationControls } from 'framer-motion';
import type { AvatarShapes } from '@frameio/vapor';
import type { FileDropZoneProps } from '@frameio/vapor-migration';
import { useAvatarButtonTransition } from '../hooks';
import { AvatarEditorDialog } from './AvatarEditorDialog';

type AvatarSettingsProps = {
  /**
   * @param props.acceptedFileTypes A string defining the filetypes the
   *  editor should accept. Identical to the `accept` attribute on an
   * <input type="file" /> element.
   *
   * Note that given a wildcard `acceptedFileTypes`, e.g., "image/*" will
   * need to assign a custom `validate` function in order to work as expected.
   * Defining a `validationErrorMessage` will also likely be necessary.
   */
  acceptedFileTypes?: string;

  /**
   * @param props.avatarSrc The URL to the image the be rendered within the
   *  avatar editor itself. This property is likely to be the `original` of
   *  the Images type on your resource, e.g., `account.images.original`.
   */
  avatarSrc?: string | null;

  /**
   * @param props.avatarThumb The URL to the thumbnail of the avatar image, e.g.,
   *  `account.images.image128`.
   */
  avatarThumb?: string | null;

  /**
   * @param props.children A render prop for children of `AvatarSettings`
   */
  children: (bind: {
    /** @param bind.controls framer-motion animation controls */
    controls: AnimationControls;

    /**
     * @param bind.isEditorOpen A boolean representing the open/closed
     *  state of the avatar editor dialog.
     */
    isEditorOpen: boolean;

    /**
     * @param bind.isTransitioning A boolean representing whether the
     *  avatar image is presently in an animation transition state.
     */
    isTransitioning: boolean;

    /** @param bind.nextAvatarSrc The URL of the incoming avatar image. */
    nextAvatarSrc: string | null;

    /**
     * @param bind.onClick On click handler for the `AvatarButton` which
     *   controls opening the avatar editor dialog.
     */
    onClick: () => void;
  }) => ReactElement<any>;

  /**
   * @param props.dropzoneLabel The label applied to the file dropzone
   *  inside the avatar editor dialog.
   */
  dropzoneLabel?: string;

  /**
   * @param props.editLabel The label text applied to the avatar editor.
   */
  editLabel?: string;

  /**
   * @param props.onSave A callback executed when a new avatar image is
   *  saved and passes the incoming image binary as an argument.
   */
  onSave: (blob: Blob) => void;

  /**
   * @param props.shape The shape the avatar button should be rendered
   *  as. Options can be either "circle" or "square".
   */
  shape: AvatarShapes;

  /**
   * @param props.validationErrorMessage The error message that should
   *  be dispalyed when an invalid file is passed to the editor.
   */
  validationErrorMessage?: string;
} & Pick<FileDropZoneProps, 'validate'>;

export function AvatarSettings({
  acceptedFileTypes,
  avatarSrc,
  avatarThumb,
  children,
  dropzoneLabel,
  editLabel,
  onSave,
  shape,
  validate,
  validationErrorMessage,
}: AvatarSettingsProps) {
  const [isEditorOpen, setIsEditorOpen] = useState(false);

  const {
    animationControls,
    handleImageChange,
    isTransitioning,
    nextImage: nextAvatarSrc,
  } = useAvatarButtonTransition({
    initNextImage: avatarThumb,
    sourceImage: avatarSrc,
    delayTransitionSeconds: (incomingSrc) => (incomingSrc ? 0.3 : 0),
  });

  const handleOnSave = useCallback(
    async (blob: Blob) => {
      const objectUrl = blob ? URL.createObjectURL(blob) : null;
      handleImageChange(objectUrl);
      onSave(blob);
      setIsEditorOpen(false);
    },
    [onSave, handleImageChange]
  );

  useEffect(() => {
    return () => {
      if (nextAvatarSrc) {
        URL.revokeObjectURL(nextAvatarSrc);
      }
    };
  }, [nextAvatarSrc]);

  return (
    <>
      <AvatarEditorDialog
        acceptedFileTypes={acceptedFileTypes}
        avatarSrc={avatarSrc}
        dropzoneLabel={dropzoneLabel}
        editLabel={editLabel}
        isShowing={isEditorOpen}
        onRequestClose={() => setIsEditorOpen(false)}
        onSave={handleOnSave}
        shape={shape}
        validate={validate}
        validationErrorMessage={validationErrorMessage}
      />
      {children({
        controls: animationControls,
        isTransitioning,
        isEditorOpen,
        nextAvatarSrc,
        onClick: () => setIsEditorOpen(true),
      })}
    </>
  );
}
